import { Alert } from '@/common/components/Alert/Alert';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { Stack } from '@/common/components/Stack/Stack';
import { Switch } from '@/common/components/Switch/Switch';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { TMemberSimulation } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { getMemberIconByAge } from '@/common/utils/family-members';
import { StringUtils } from '@/common/utils/string';
import { moneyToString } from '@coverflex-tech/hypernova-utils';

export const SimulateFamilyMemberCard = ({
  person,
  onToggle,
  onRemove,
  isLoading,
  ...cardProps
}: SimulateFamilyMemberCardProps) => {
  return (
    <CardAction
      {...cardProps}
      size="sm"
      title={
        <Label bold color={person.error === null ? 'neutral30' : 'neutral20'}>
          {person.shortName || StringUtils.getFirstName(person?.fullName)}
        </Label>
      }
      description={
        person.error === null && (
          <Stack>
            {person.paidByEmployee?.amount > 0 && (
              <Label size="sm" color="neutral20">
                <Translate
                  i18nKey="dashboard.insurance_detail.simulate_family_modal.member.paid_by_employee_price"
                  values={{
                    price: moneyToString(person.paidByEmployee),
                  }}
                />
              </Label>
            )}
            {person.paidByCompany?.amount > 0 && (
              <Label size="sm" color="neutral20">
                <Translate
                  i18nKey="dashboard.insurance_detail.simulate_family_modal.member.paid_by_company_price"
                  values={{
                    price: moneyToString(person.paidByCompany),
                  }}
                />
              </Label>
            )}
          </Stack>
        )
      }
      icon={getMemberIconByAge(person.birthdate)}
      action={
        <Stack horizontal gap="md" justify="center" align="center">
          <Switch
            checked={person.selected}
            variant="simple"
            onChange={() => {
              if (person.error === null) {
                onToggle({ ...person, selected: !person.selected });
              }
            }}
            disabled={person.error !== null || isLoading}
          />
          <Button
            variant="tertiary"
            icon={<Icon icon={['far', 'trash-can']} />}
            size="sm"
            disabled={!person.isUserAdded || isLoading}
            onPress={() => {
              return onRemove(person);
            }}
          />
        </Stack>
      }
    >
      {person.error && (
        <Alert type="error" noBackground>
          <Translate
            parent={BodyText}
            color="systemError"
            i18nKey="dashboard.insurance_detail.simulate_family_modal.member.error"
            components={{
              strong: (
                <BodyText as="strong" bold color="systemError" underline />
              ),
              button: (
                <TextAction onPress={() => AnalyticsService.openChat()} />
              ),
            }}
          />
        </Alert>
      )}
    </CardAction>
  );
};

export type SimulateFamilyMemberCardProps = {
  checked?: boolean;
  onToggle: (person: TMemberSimulation) => void;
  onRemove: (person: TMemberSimulation) => void;
  person: TMemberSimulation;
  isLoading?: boolean;
};
